import { Header, Popup, Tab, Table } from 'semantic-ui-react';
import { CORE_VALUE_CHANGE_METRICS, GENERAL_QUALITY_METRICS, QUALITY_METRICS } from '../../utils/qualityBreakdown';
import { getQualityMetricDetails } from '../../utils/qualityMetricDetails';
import type { adjustedUserGroupedMistakeKPIs } from '../../utils/qualityUtils';
import './qualityStatisticsTable.css';

const QUALITY_CATEGORIES = [
    { key: 'updates', label: 'Updates', scopeKey: 'updatesScope' },
    { key: 'fullReviews', label: 'Full Check Reviews', scopeKey: 'fullReviewsScope' },
    { key: 'coreDataReviews', label: 'Core Data Reviews (Mistake Detection)', scopeKey: 'coreDataReviewsScope' },
    { key: 'incrementalCoreDataReviews', label: 'Core Data Reviews (Regular Update)', scopeKey: 'incrementalCoreDataReviewsScope' },
    { key: 'totalCoreDataReviews', label: 'Core Data Reviews (Total)', scopeKey: 'totalCoreDataReviewsScope' },
    { key: 'total', label: 'Total exc. Core Data Reviews', scopeKey: 'totalScope' },
    { key: 'detectedMistakesAsQA', label: 'Detected Mistakes as QA', scopeKey: 'detectedMistakesAsQAScope' },
] as const;

type QualityKpiRow = ReturnType<typeof adjustedUserGroupedMistakeKPIs>[number];

type QualityStatisticsTableProps = {
    rows: (QualityKpiRow & {
        userLabel: string;
        averageBreakdowns?: QualityKpiRow['breakdowns'];
    })[];
};

export default function QualityStatisticsTable({ rows }: QualityStatisticsTableProps) {
    const panes = QUALITY_CATEGORIES.map(category => ({
        menuItem: category.label,
        render: () => <Tab.Pane key={category.key}>
            <div
                className='quality-statistics__scroll'
                role='region'
                aria-label={`${category.label} statistics`}
                tabIndex={0}
            >
                <Table striped selectable celled unstackable className='quality-statistics__table'>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell scope='col' rowSpan={2} className='quality-statistics__user'>User</Table.HeaderCell>
                            {GENERAL_QUALITY_METRICS.filter(metric => metric.key !== 'affectedRecords').map(({ key, label }) => <Table.HeaderCell key={key} scope='col' rowSpan={2}>
                                {label}
                            </Table.HeaderCell>)}
                            <Table.HeaderCell scope='colgroup' colSpan={CORE_VALUE_CHANGE_METRICS.length} textAlign='center'>
                                Core Value Change Kinds
                            </Table.HeaderCell>
                            <Table.HeaderCell scope='col' rowSpan={2}>Affected Records</Table.HeaderCell>
                        </Table.Row>
                        <Table.Row>
                            {CORE_VALUE_CHANGE_METRICS.map(({ key, label }) => <Table.HeaderCell key={key} scope='col'>
                                {label}
                            </Table.HeaderCell>)}
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {rows.map(row => {
                            const breakdown = row.breakdowns[category.key];
                            const averageBreakdown = row.averageBreakdowns?.[category.key];

                            return <Table.Row key={row.userId}>
                                <Table.Cell className='quality-statistics__user'>
                                    {row.userLabel}
                                </Table.Cell>
                                {QUALITY_METRICS.map(({ key, label }) => {
                                    const details = getQualityMetricDetails(breakdown, key, averageBreakdown);
                                    const value = breakdown[key].per100Cells;
                                    const averageValue = averageBreakdown?.[key].per100Cells;

                                    return <Table.Cell
                                        key={key}
                                        textAlign='right'
                                        positive={value === 0}
                                        negative={value !== undefined && averageValue !== undefined && value > averageValue}
                                    >
                                        <Popup
                                            on='click'
                                            size='small'
                                            header={label}
                                            content={<>
                                                <div>Cells Scope: {row[category.scopeKey]}</div>
                                                <Table compact celled size='small'>
                                                    <Table.Header>
                                                        <Table.Row>
                                                            <Table.HeaderCell>Total</Table.HeaderCell>
                                                            <Table.HeaderCell>Contrib</Table.HeaderCell>
                                                            {averageBreakdown && <Table.HeaderCell>vs Avg</Table.HeaderCell>}
                                                        </Table.Row>
                                                    </Table.Header>
                                                    <Table.Body>
                                                        <Table.Row>
                                                            <Table.Cell textAlign='right'>{details.total}</Table.Cell>
                                                            <Table.Cell textAlign='right'>{details.contribution}</Table.Cell>
                                                            {averageBreakdown && <Table.Cell textAlign='right' className={details.vsAverageClass}>
                                                                {details.vsAverage}
                                                            </Table.Cell>}
                                                        </Table.Row>
                                                    </Table.Body>
                                                </Table>
                                            </>}
                                            trigger={<div
                                                className='quality-statistics__value'
                                                aria-label={`${label}: ${details.per100}; show details`}
                                            >
                                                {details.per100}
                                            </div>}
                                        />
                                    </Table.Cell>;
                                })}
                            </Table.Row>;
                        })}
                    </Table.Body>
                </Table>
            </div>
        </Tab.Pane>,
    }));

    return <section className='quality-statistics' aria-label='Quality statistics per 100 cells'>
        <Header as='h3'>Quality Statistics Per 100 Cells</Header>
        <Tab
            menu={{ secondary: true, pointing: true, className: 'quality-statistics__tabs' }}
            panes={panes}
        />
    </section>;
}
